import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { CertificationInterface } from 'src/app/model/certifications/certification-interface';
import { IssuerCredentialDefinition } from '../models/issuer-api.models';
import { IssuerCertificationCatalogueApiService } from './issuer-certification-catalogue-api.service';
import { IssuerCertificationMapper } from './issuer-certification.mapper';

const ISSUER_PARTICIPANT_CONTEXT_ID = 'con-x-issuer';

@Injectable({ providedIn: 'root' })
export class IssuerCertificationCollectionService {
    private readonly collectionSubject = new BehaviorSubject<
        CertificationInterface[]
    >([]);
    private readonly errorSubject = new BehaviorSubject<unknown | null>(null);
    private loadGeneration = 0;

    readonly collection$: Observable<CertificationInterface[]> =
        this.collectionSubject.asObservable();
    readonly error$: Observable<unknown | null> =
        this.errorSubject.asObservable();

    constructor(
        private readonly catalogueApi: IssuerCertificationCatalogueApiService,
        private readonly mapper: IssuerCertificationMapper
    ) {}

    get collection(): CertificationInterface[] {
        return [...this.collectionSubject.value];
    }

    /**
     * Loads the credential-definition catalogue for the active certification
     * provider and projects it into BIMSWARM's certification hierarchy.
     *
     * Participant-context-to-organisation resolution is intentionally isolated
     * here until that mapping is agreed and supplied by the backend.
     */
    async loadForActiveOrganisation(
        organisationId: number
    ): Promise<CertificationInterface[]> {
        const generation = ++this.loadGeneration;

        try {
            const definitions = await firstValueFrom(
                this.catalogueApi.getCredentialDefinitions(
                    ISSUER_PARTICIPANT_CONTEXT_ID
                )
            );
            const certifications = definitions
                .filter(
                    (
                        definition
                    ): definition is IssuerCredentialDefinition =>
                        Boolean(
                            definition?.id &&
                                definition.credentialType &&
                                definition.format &&
                                definition.jsonSchema
                        )
                )
                .map((definition) =>
                    this.mapper.toCertification(
                        definition,
                        organisationId,
                        ISSUER_PARTICIPANT_CONTEXT_ID
                    )
                )
                .sort((left, right) =>
                    (left.name ?? '').localeCompare(right.name ?? '')
                );

            if (generation === this.loadGeneration) {
                this.collectionSubject.next(certifications);
                this.errorSubject.next(null);
            }
            return [...certifications];
        } catch (error) {
            if (generation === this.loadGeneration) {
                this.errorSubject.next(error);
            }
            throw error;
        }
    }

    clear(): void {
        this.loadGeneration++;
        this.collectionSubject.next([]);
        this.errorSubject.next(null);
    }
}
